import React, { useState, useEffect } from 'react';
import { Card, Button, Typography } from 'antd';
import {
  BellOutlined,
  CloseOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  UserOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { Notification } from '../services/api';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import './NotificationPopup.css';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;

interface NotificationPopupProps {
  notification: Notification;
  onClose: () => void;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
}

const NotificationPopup: React.FC<NotificationPopupProps> = ({
  notification,
  onClose,
  onMarkAsRead,
  onDelete
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Animate in after component mounts
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match CSS transition duration
  };

  const handleMarkAsRead = () => {
    onMarkAsRead(notification.id);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(notification.id);
    handleClose();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'assignment':
      case 'batch_assignment':
        return <UserOutlined />;
      case 'duplicate_lead':
        return <ExclamationCircleOutlined />;
      case 'activity':
      case 'event_creation':
        return <CalendarOutlined />;
      case 'reminder':
        return <CalendarOutlined />;
      case 'info':
        return <InfoCircleOutlined />;
      case 'warning':
        return <ExclamationCircleOutlined />;
      case 'error':
        return <ExclamationCircleOutlined />;
      default:
        return <BellOutlined />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    // Get current time in Lisbon timezone
    const now = dayjs().tz('Europe/Lisbon');
    // Parse the notification time (assuming it's already in Lisbon time from backend)
    const notificationTime = dayjs(dateString).tz('Europe/Lisbon');
    const diffInMinutes = now.diff(notificationTime, 'minute');
    
    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `Há ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Há ${Math.floor(diffInMinutes / 60)}h`;
    return `Há ${Math.floor(diffInMinutes / 1440)}d`;
  };

  return (
    <div 
      className={`notification-popup ${isVisible ? 'visible' : ''} ${isClosing ? 'closing' : ''}`}
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        maxWidth: '400px',
        minWidth: '320px',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        opacity: isVisible ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Card
        size="small"
        className="notification-popup-card"
        style={{
          borderRadius: '16px',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12), 0 12px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          background: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
        bodyStyle={{ padding: '16px' }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#fef2f2',
                color: '#dc2626',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
              }}
            >
              {getTypeIcon(notification.notification_type)}
            </span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {formatTimeAgo(notification.created_at)}
            </span>
          </div>
          <Button
            type="text"
            icon={<CloseOutlined />}
            size="small"
            onClick={handleClose}
            style={{
              color: '#8c8c8c',
              border: 'none',
              background: 'none',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </div>

        {/* Content */}
        <div style={{ marginBottom: '12px' }}>
          <Title level={5} style={{ 
            margin: '0 0 8px 0', 
            fontSize: '14px',
            fontWeight: 600,
            color: '#262626',
            lineHeight: '1.4'
          }}>
            {notification.title}
          </Title>
          <Text style={{ 
            fontSize: '13px', 
            color: '#595959',
            lineHeight: '1.5',
            display: 'block'
          }}>
            {notification.message}
          </Text>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '8px',
          borderTop: '1px solid #f1f5f9'
        }}>
          <div>
            {!notification.read && (
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                onClick={handleMarkAsRead}
                className="notification-popup-mark-read"
                style={{
                  color: '#6b7280',
                  fontSize: '12px',
                  height: '24px',
                  padding: '0 8px'
                }}
              >
                Marcar como lida
              </Button>
            )}
          </div>
          <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
            {dayjs(notification.created_at).tz('Europe/Lisbon').format('HH:mm')}
          </Text>
        </div>

        {/* Header accent bar */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: '16px 16px 0 0'
          }}
        />
        
      </Card>
    </div>
  );
};

export default NotificationPopup;
